/**
 * Thin Segment CDP abstraction for Phoebe chat widget.
 *
 * Supports two modes:
 *
 * 1. **IIFE / script-tag mode** (phoenix.edu):
 *    Segment Analytics.js is already loaded on the host page as `window.analytics`.
 *    Call `initFromWindow()` — no write key needed, no SDK bundled.
 *
 * 2. **npm library mode** (`@uopx/phoebe-chat` React consumers):
 *    Consumer passes `analyticsWriteKey` prop. Call `init(writeKey)` which
 *    lazily loads `@segment/analytics-next` and initialises a private instance.
 *
 * ### Identity
 *
 * Segment auto-assigns a stable UUID v4 `anonymousId` (stored in `ajs_anonymous_id`
 * cookie and localStorage) on first visit. No `alias()` call is needed — the
 * widget relies entirely on Segment's own anonymous ID management.
 *
 * ### Traits retrieval
 *
 * `getTraits()` reads traits the host page may have stored via `analytics.identify()`
 * (stored in `ajs_user_traits` in localStorage). Use this to retrieve `email` and
 * `enrollmentStatus` when a conversation starts.
 *
 * ### Async-init call queuing
 *
 * When `init(writeKey)` or `initFromWindow(writeKey)` starts an async
 * `@segment/analytics-next` load, any `track()` calls made before the load
 * completes are queued and replayed against the real instance once it is ready.
 * This ensures that `Live Chat Conversation Started` — which often fires within
 * milliseconds of mount — is never silently dropped to the consoleFallback.
 *
 * `initFromWindow()` without a write key resolves synchronously (the instance
 * is either `window.analytics` or the consoleFallback immediately), so no
 * queuing is needed in the IIFE-without-write-key path.
 *
 * `disableAnalytics` on either `__PHOEBE_CONFIG__` or `ChatModalProps` skips
 * all calls entirely (no stub, no network).
 */

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Properties for Segment Live Chat semantic events.
 * https://www.twilio.com/docs/segment/connections/spec/live-chat
 *
 * Message/search text is never sent to CDP. Visitor-entered chat text can
 * contain sensitive personal information, so `track()` strips message/search
 * payload fields even if a caller passes them accidentally.
 */
export type LiveChatEventProperties = {
  /** The persistent conversation / thread identifier. Maps to Phoebe's thread_id. */
  conversation_id?: string;
  /**
   * The routed department domain (e.g. "knowledge/dotedu-webscrape").
   * Closest equivalent to agent_id in Phoebe's model.
   */
  agent_id?: string;
  /** Human-readable agent name returned in agent_metadata.routed_to.agent_name. */
  agent_name?: string;
  /** Per-message identifier. clientMessageId for sent; trace_id for received. */
  message_id?: string;
  /** Elapsed seconds since the conversation started. */
  conversation_duration?: number;
};

export type LiveChatEventName =
  | "Live Chat Conversation Started"
  | "Live Chat Conversation Ended"
  | "Live Chat Message Sent"
  | "Live Chat Message Received";

/**
 * Traits stored in Segment's `ajs_user_traits` localStorage key by the host
 * page via `analytics.identify()`. The widget reads these traits on each
 * conversation start via `getTraits()` but never writes them.
 */
export type SegmentTraits = {
  email?: string;
  enrollmentStatus?: string;
  [key: string]: unknown;
};

type AnalyticsInstance = {
  /**
   * Retained for structural compatibility with window.analytics / AnalyticsBrowser.
   * Not exposed as a public API — Phoebe does not call identify() (no PII).
   * @internal
   */
  identify(userId: string): void;
  track(event: string, properties?: Record<string, unknown>): void;
  /** Returns the underlying User object for traits/anonymousId access. */
  user?(): { traits(): SegmentTraits | undefined } | undefined;
};

type QueuedCall = { kind: "track"; event: string; properties?: Record<string, unknown> };

const CHAT_TEXT_PROPERTY_NAMES = new Set(["message_body", "searchText", "search_text"]);

// ── Internal state ────────────────────────────────────────────────────────────

let _instance: AnalyticsInstance | null = null;
let _disabled = false;

/**
 * Set while an async library load is in progress.
 * `track()` calls append to `_queue` instead of falling through to
 * consoleFallback. The promise resolves once `_instance` is populated and the
 * queue is flushed.
 */
let _initPromise: Promise<void> | null = null;

/** Calls buffered during an in-flight async init. */
const _queue: QueuedCall[] = [];

/**
 * Monotonically increasing generation counter. Incremented by `disable()` and
 * `_reset()` so that any in-flight `_initLibrary` call can detect it has been
 * superseded and bail without flushing the (already-cleared) queue.
 */
let _generation = 0;

// ── Console stub ──────────────────────────────────────────────────────────────

/**
 * Console-log stub used when no real Segment instance is available.
 * Provides local-dev visibility without requiring a write key.
 */
const consoleFallback: AnalyticsInstance = {
  identify(userId: string) {
    console.log("[phoebe-analytics] identify", userId);
  },
  track(event: string, properties?: Record<string, unknown>) {
    console.log("[phoebe-analytics] track", event, properties ?? {});
  },
};

// ── Queue helpers ─────────────────────────────────────────────────────────────

function _flushQueue(target: AnalyticsInstance): void {
  let call: QueuedCall | undefined;
  while ((call = _queue.shift()) !== undefined) {
    try {
      target.track(call.event, call.properties);
    } catch (err) {
      console.warn("[phoebe-analytics] error replaying queued call", err);
    }
  }
}

function sanitizeProperties(properties?: LiveChatEventProperties): Record<string, unknown> | undefined {
  if (!properties) return undefined;
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties as Record<string, unknown>)) {
    if (CHAT_TEXT_PROPERTY_NAMES.has(key)) continue;
    sanitized[key] = value;
  }
  return sanitized;
}

// ── Initialisation ────────────────────────────────────────────────────────────

/**
 * Initialise from the host page's `window.analytics` (IIFE mode).
 *
 * If a `segmentWriteKey` is provided, skip `window.analytics` and load
 * `@segment/analytics-next` instead (useful for hybrid / override cases).
 * Calls made before the async load completes are queued and replayed.
 *
 * Falls back to the console stub if neither is available (synchronously).
 */
export function initFromWindow(segmentWriteKey?: string): void {
  if (_disabled) return;
  if (_instance || _initPromise) return; // already initialised or in-flight

  if (segmentWriteKey) {
    // Write key takes precedence over window.analytics even in IIFE mode.
    // Start async load; calls made in the meantime will be queued.
    _initPromise = _initLibrary(segmentWriteKey);
    return;
  }

  const win = globalThis as unknown as { analytics?: AnalyticsInstance };
  if (win.analytics && typeof win.analytics.identify === "function") {
    _instance = win.analytics;
  } else {
    // window.analytics not ready — install console stub synchronously.
    _instance = consoleFallback;
  }
}

/**
 * Initialise with an explicit Segment write key (npm library mode).
 *
 * Lazily imports `@segment/analytics-next` so it is never bundled into the
 * IIFE build unless explicitly called. Calls made before the load resolves
 * are queued and replayed against the real instance once ready.
 * Falls back to consoleFallback on error.
 */
export function init(writeKey: string): Promise<void> {
  if (_disabled) return Promise.resolve();
  if (_instance) return Promise.resolve(); // already ready
  if (_initPromise) return _initPromise; // deduplicate concurrent calls
  _initPromise = _initLibrary(writeKey);
  return _initPromise;
}

async function _initLibrary(writeKey: string): Promise<void> {
  const generation = _generation;
  try {
    const { AnalyticsBrowser } = await import("@segment/analytics-next");
    const [analytics] = await AnalyticsBrowser.load({ writeKey });
    // If disable() or _reset() was called while we were loading, abort silently.
    if (_generation !== generation) return;
    _instance = {
      identify: (userId) => void analytics.identify(userId),
      track: (event, properties) => void analytics.track(event, properties),
      // `user()` exists on Analytics.js 2.x / @segment/analytics-next but is
      // not declared in the exported TypeScript types, hence the cast.
      user: () => (analytics as unknown as { user?(): { traits(): SegmentTraits | undefined } }).user?.(),
    };
  } catch (err) {
    if (_generation !== generation) return;
    console.warn("[phoebe-analytics] failed to load @segment/analytics-next, falling back to console stub", err);
    _instance = consoleFallback;
  } finally {
    if (_generation === generation) {
      // Flush any calls that were queued while we were loading.
      // _instance is guaranteed non-null here (either real or consoleFallback).
      if (_instance) _flushQueue(_instance);
      _initPromise = null;
    }
  }
}

/**
 * Disable all analytics calls. No-op everything, no console output.
 *
 * **Important:** This suppresses both Segment CDP and UOPX DataLayer tracking.
 * When `disableAnalytics: true` is set via `__PHOEBE_CONFIG__` or `ChatModalProps`,
 * all `track()` and `trackUOPX()` calls become silent no-ops.
 *
 * Call before `init` / `initFromWindow` to take effect.
 */
export function disable(): void {
  _disabled = true;
  _instance = null;
  _initPromise = null;
  _queue.length = 0;
  _generation++; // invalidate any in-flight _initLibrary call
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Read traits the host page has stored in Segment via `analytics.identify()`.
 * Traits are stored in `ajs_user_traits` in localStorage and are available
 * without any additional calls from the widget.
 *
 * Returns `{}` when no traits are present, when analytics is disabled, or when
 * the instance has not yet been initialised. Never throws.
 *
 * Call this when a conversation starts to read `email` and `enrollmentStatus`.
 */
export function getTraits(): SegmentTraits {
  if (_disabled) return {};
  try {
    // npm library mode: access via the wrapped user() method on _instance.
    if (_instance?.user) {
      return _instance.user()?.traits() ?? {};
    }
    // IIFE / window.analytics mode: access via window.analytics.user().
    const win = globalThis as unknown as {
      analytics?: { user?: () => { traits?: () => SegmentTraits | undefined } | undefined };
    };
    return win.analytics?.user?.()?.traits?.() ?? {};
  } catch {
    return {};
  }
}

/**
 * Fire a Segment Live Chat semantic track event.
 *
 * https://www.twilio.com/docs/segment/connections/spec/live-chat
 *
 * If called while an async init is in progress, the call is queued and
 * replayed against the real Segment instance once it is ready.
 */
export function track(event: LiveChatEventName, properties?: LiveChatEventProperties): void {
  if (_disabled) return;
  const sanitizedProperties = sanitizeProperties(properties);
  if (_initPromise) {
    // Async load in progress — queue so it is replayed against the real instance.
    _queue.push({ kind: "track", event, properties: sanitizedProperties });
    return;
  }
  const target = _instance ?? consoleFallback;
  try {
    target.track(event, sanitizedProperties);
  } catch (err) {
    console.warn("[phoebe-analytics] track error", err);
  }
}

/**
 * Reset internal state. Intended for testing only.
 * @internal
 */
export function _reset(): void {
  _instance = null;
  _disabled = false;
  _initPromise = null;
  _queue.length = 0;
  _generation++;
}

// ── UOPX DataLayer ────────────────────────────────────────────────────────────

/**
 * UOPX DataLayer interface (separate from Segment CDP).
 * Used for interaction tracking on phoenix.edu.
 *
 * **Security Note:** `debugUOPXTracking` is an opt-in debug feature that logs
 * event data to the browser console when `window.uopxDataLayer` is absent.
 * Only enable this in local development, QA, or staging environments.
 * Do not enable in production as it exposes event metadata to browser console,
 * extensions, and monitoring tools.
 *
 * **Note:** The full `Window.__PHOEBE_CONFIG__` interface (including `debugUOPXTracking`)
 * is declared in `src/iife.ts`. This module uses a local type assertion to access
 * the debug flag without redeclaring the interface.
 */
declare global {
  interface Window {
    uopxDataLayer?: {
      track(eventName: string, eventData: Record<string, unknown>): void;
    };
  }
}

/**
 * Track an event to UOPX DataLayer.
 * Silent no-op when window.uopxDataLayer is unavailable (unless debugUOPXTracking is enabled).
 *
 * **Important:** This function respects the shared `_disabled` flag set by
 * `analytics.disable()` or the `disableAnalytics` prop. When analytics are disabled,
 * both Segment CDP (`track()`) and UOPX DataLayer (`trackUOPX()`) are suppressed.
 * This ensures consistent opt-out behavior across all tracking systems.
 *
 * @param eventName - Event name (e.g., "ChatWithAgent", "superPhoebeChipClick")
 * @param text - Descriptive text for the event (e.g., "Phoebe - AI assistant")
 * @param location - Location identifier (e.g., "superPhoebeChat : Icon")
 * @param additionalData - Optional additional data to merge into the event payload
 */
export function trackUOPX(eventName: string, text: string, location: string, additionalData?: Record<string, unknown>): void {
  // Respects analytics.disable() / disableAnalytics for both Segment CDP and UOPX DataLayer.
  if (_disabled) return;

  const data = {
    ...additionalData,
    componentName: "superPhoebeChat",
    text,
    location,
  };

  try {
    if (typeof window !== "undefined" && window.uopxDataLayer?.track) {
      window.uopxDataLayer.track(eventName, data);
    } else if (typeof window !== "undefined" && (window as { __PHOEBE_CONFIG__?: { debugUOPXTracking?: boolean } }).__PHOEBE_CONFIG__?.debugUOPXTracking) {
      // Development-mode console fallback when DataLayer is absent
      console.log("[uopx-datalayer] track", eventName, data);
    }
    // Silent no-op when DataLayer is absent and debug mode is off (production security policy)
  } catch (err) {
    // Log only error message, not full error object which may contain event data
    const message = err instanceof Error ? err.message : String(err);
    console.warn("[uopx-datalayer] track error:", message);
  }
}
